import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/auth';
import MyTripsList from '@/components/trips/MyTripsList';
import EmptyTripsState from '@/components/trips/EmptyTripsState';
import TripHeader from '@/components/trips/TripHeader';
import TripSearchBar from '@/components/trips/TripSearchBar';
import EmptySearchResults from '@/components/trips/EmptySearchResults';
import { useTrips } from '@/components/trips/useTrips';
import TripFiltersComponent from '@/components/trips/TripFilters';
import JoinTripModal from '@/components/JoinTripModal';

const MyTrips: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  
  const { 
    filteredTrips, 
    trips, 
    isLoading, 
    searchTerm, 
    setSearchTerm, 
    handleTripDeleted,
    stats,
    filters,
    setFilters,
    resetFilters,
    activeFilterCount
  } = useTrips();

  useEffect(() => {
    // Redirect to auth page if user is not logged in
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  const handleCreateTrip = () => {
    navigate('/dashboard');
  };
  
  const handleJoinTrip = () => {
    setJoinModalOpen(true);
  };

  // Show loading state
  if (authLoading || isLoading) {
    return (
      <div className="container mx-auto px-4 py-16 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-glowingTeal mb-4" />
        <p className="text-muted-foreground">Loading your trips...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-6">
        <TripHeader 
          onCreateTrip={handleCreateTrip}
          tripCount={stats.totalTrips}
          upcomingTrips={stats.upcomingTrips}
        />
        <TripSearchBar 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onCreateTrip={handleCreateTrip}
          onJoinTrip={handleJoinTrip}
        />
      </div>

      <div className="mb-8">
        <TripFiltersComponent 
          filters={filters}
          onFilterChange={setFilters}
          onResetFilters={resetFilters}
          activeFilterCount={activeFilterCount}
        />
      </div>

      {trips.length > 0 ? (
        filteredTrips.length > 0 ? (
          <MyTripsList 
            trips={filteredTrips} 
            onTripDeleted={handleTripDeleted} 
          />
        ) : (
          <EmptySearchResults onResetFilters={resetFilters} />
        )
      ) : (
        <EmptyTripsState onCreateTrip={handleCreateTrip} />
      )}
      
      <JoinTripModal open={joinModalOpen} onOpenChange={setJoinModalOpen} />
    </div>
  );
};

export default MyTrips;
